import { apiClient } from '@/auth';

/**
 * RxAssistant — clinician-facing AI chat that answers questions about the
 * patient's data. V2 port of v1's `AdvisorChatService`
 * (clinician/src/services/AdvisorChatService.ts).
 *
 * 6 endpoints, all under `/patients/{id}/advisor-chat`. Schemas verified
 * against MNT swagger:1401-1581, 13882-13981.
 *
 *   GET    .../sessions                                  — list sessions
 *   POST   .../sessions?session_type=clinician           — create + send first msg
 *   GET    .../sessions/{sid}                            — get session w/ messages
 *   POST   .../sessions/{sid}                            — send msg to existing session
 *   GET    .../sessions/subject/{type}/{id}              — resume session by subject key
 *   GET    .../question-suggestions                      — daily suggestion chips
 */

export type AdvisorChatSubjectType =
  | 'general'
  | 'patient_daily_insight'
  | 'sms_message'
  | 'pinned_health_targets'
  | 'client_prompt'
  | 'meal_reflection';

export type AdvisorChatRole = 'user' | 'assistant';
export type AdvisorChatContentType = 'text/plain' | 'text/markdown';

/** Wire-format. v1 transforms snake_case → camelCase before handing off to
 *  the UI; we keep the same convention so the UI port is line-for-line. */
export type AdvisorChatMessageResponse = {
  id: number;
  session_id: number;
  role: AdvisorChatRole;
  content_type: AdvisorChatContentType;
  title?: string | null;
  content: string;
  timestamp: string;
  has_follow_up_question?: boolean | null;
};

export type AdvisorChatSessionResponse = {
  id: number;
  user_id: number;
  patient_id: number;
  subject_type: AdvisorChatSubjectType;
  subject_id?: string | null;
  title?: string | null;
  messages: {
    rows: AdvisorChatMessageResponse[];
    has_more: boolean;
    message_next_timestamp?: string | null;
  };
  created_time: string;
  updated_time: string;
};

export type AdvisorChatSessionSummaryResponse = {
  id: number;
  user_id: number;
  patient_id: number;
  subject_type: AdvisorChatSubjectType;
  subject_id?: string | null;
  title?: string | null;
  latest_message?: AdvisorChatMessageResponse | null;
  oldest_message?: AdvisorChatMessageResponse | null;
  created_time: string;
  updated_time: string;
};

export type AdvisorChatSessionsResponse = {
  rows: AdvisorChatSessionSummaryResponse[];
  has_more: boolean;
  next_timestamp?: string | null;
};

export type AdvisorQuestionSuggestionResponse = {
  id: number;
  patient_id: number;
  question_date: string;
  question: string;
};

/** Request body for POST `.../sessions` and POST `.../sessions/{sid}`. */
export type AdvisorChatMessageRequest = {
  content: string;
  start_mode?: 'user_message' | 'assistant_question' | 'assistant_meal_reflection' | null;
  opening_question?: string | null;
  meal_id?: number | null;
  client_subject_type?: string | null;
  subject_type?: AdvisorChatSubjectType;
  subject_id?: string | null;
  /** Date-range filter that constrains the LLM's view of the patient's data
   *  to the dates the clinician is currently looking at. Mirrors v1's
   *  `chatContextToDict` (clinician/src/services/AdvisorChatService.ts:79). */
  context_options?: {
    filter_dates_lte?: string | null;
    filter_dates_gte?: string | null;
  } | null;
};

/* -------------------------------------------------------------------------- */
/*                          CAMELCASE VIEW SHAPES                             */
/* -------------------------------------------------------------------------- */

export type ChatMessage = {
  id: number;
  sessionId: number;
  timestamp: string;
  role: AdvisorChatRole;
  content: string;
  contentType: AdvisorChatContentType;
  /** Set true while a user-typed message is in flight; cleared on success
   *  (replaced by the server's persisted assistant reply). */
  isOptimistic?: boolean;
};

export type ChatSession = {
  id: number;
  userId: number;
  patientId: number;
  subjectType?: AdvisorChatSubjectType | null;
  subjectId?: string | null;
  title: string;
  messages: ChatMessage[];
  createdTime: string;
  updatedTime: string;
  hasMoreMessages: boolean;
  nextTimestamp?: string | null;
};

export type ChatContext = {
  filterDatesLte?: string | null;
  filterDatesGte?: string | null;
};

const transformMessage = (data: AdvisorChatMessageResponse): ChatMessage => ({
  id: data.id,
  sessionId: data.session_id,
  timestamp: data.timestamp,
  role: data.role,
  content: data.content || data.title || '',
  contentType: data.content_type,
});

const transformSession = (data: AdvisorChatSessionResponse): ChatSession => ({
  id: data.id,
  userId: data.user_id,
  patientId: data.patient_id,
  subjectType: data.subject_type,
  subjectId: data.subject_id ?? null,
  title: data.title ?? '',
  messages: (data.messages?.rows ?? []).map(transformMessage),
  createdTime: data.created_time,
  updatedTime: data.updated_time,
  hasMoreMessages: data.messages?.has_more ?? false,
  nextTimestamp: data.messages?.message_next_timestamp ?? null,
});

const chatContextToDict = (context?: ChatContext): AdvisorChatMessageRequest['context_options'] => {
  if (!context) return null;
  return {
    filter_dates_lte: context.filterDatesLte ?? null,
    filter_dates_gte: context.filterDatesGte ?? null,
  };
};

/* -------------------------------------------------------------------------- */
/*                                 CLIENT                                     */
/* -------------------------------------------------------------------------- */

export const advisorChatApi = {
  /** Create a new chat session and post the first message. v1 forces
   *  `session_type=clinician` so the server uses the clinician-portal
   *  prompt shaping rather than the patient app's. */
  async createSession(args: {
    patientId: number;
    initialMessage: string;
    subjectType: AdvisorChatSubjectType;
    subjectId?: string;
    context?: ChatContext;
  }): Promise<ChatMessage> {
    const body: AdvisorChatMessageRequest = {
      content: args.initialMessage,
      subject_type: args.subjectType,
      subject_id: args.subjectId ?? null,
      context_options: chatContextToDict(args.context),
    };
    const res = await apiClient.post<AdvisorChatMessageResponse>(
      `/patients/${args.patientId}/advisor-chat/sessions`,
      body,
      { params: { session_type: 'clinician' } },
    );
    return transformMessage(res.data);
  },

  /** Post a message to an existing session. Server returns the assistant
   *  reply directly (synchronous-look-and-feel; the LLM completion is
   *  awaited server-side before responding). */
  async sendMessage(args: {
    patientId: number;
    sessionId: number;
    content: string;
  }): Promise<ChatMessage> {
    const res = await apiClient.post<AdvisorChatMessageResponse>(
      `/patients/${args.patientId}/advisor-chat/sessions/${args.sessionId}`,
      { content: args.content },
    );
    return transformMessage(res.data);
  },

  async getSession(args: {
    patientId: number;
    sessionId: number;
    messageLimit?: number;
    messageNextTimestamp?: string;
  }): Promise<ChatSession | null> {
    try {
      const res = await apiClient.get<AdvisorChatSessionResponse>(
        `/patients/${args.patientId}/advisor-chat/sessions/${args.sessionId}`,
        {
          params: {
            message_limit: args.messageLimit,
            message_next_timestamp: args.messageNextTimestamp,
          },
        },
      );
      return transformSession(res.data);
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      // 404 = session was never created or is gone — return null so the UI
      // falls through to the welcome state instead of erroring.
      if (status === 404) return null;
      throw err;
    }
  },

  /** Resume by subject key — used to restart the same conversation across
   *  page loads when the popper has a stable subject (e.g. a meal id). */
  async getSessionBySubject(args: {
    patientId: number;
    subjectType: AdvisorChatSubjectType;
    subjectId: string;
    messageLimit?: number;
    messageNextTimestamp?: string;
  }): Promise<ChatSession | null> {
    try {
      const res = await apiClient.get<AdvisorChatSessionResponse>(
        `/patients/${args.patientId}/advisor-chat/sessions/subject/${encodeURIComponent(args.subjectType)}/${encodeURIComponent(args.subjectId)}`,
        {
          params: {
            message_limit: args.messageLimit,
            message_next_timestamp: args.messageNextTimestamp,
            session_type: 'clinician',
          },
        },
      );
      return transformSession(res.data);
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) return null;
      throw err;
    }
  },

  async getSessions(args: {
    patientId: number;
    subjectType?: AdvisorChatSubjectType;
    limit?: number;
    nextTimestamp?: string;
  }): Promise<AdvisorChatSessionsResponse> {
    try {
      const res = await apiClient.get<AdvisorChatSessionsResponse>(
        `/patients/${args.patientId}/advisor-chat/sessions`,
        {
          params: {
            subject_type: args.subjectType,
            limit: args.limit,
            next_timestamp: args.nextTimestamp,
            session_type: 'clinician',
          },
        },
      );
      return res.data && Array.isArray(res.data.rows) ? res.data : { rows: [], has_more: false };
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status !== undefined && status >= 400 && status < 500) {
        return { rows: [], has_more: false };
      }
      throw err;
    }
  },

  /** Daily server-suggested questions for the welcome state. Server caches
   *  per patient per day; force_rebuild bypasses the cache. */
  async getQuestionSuggestions(args: {
    patientId: number;
    questionDate?: string;
    subjectType?: AdvisorChatSubjectType;
    subjectId?: string;
    forceRebuild?: boolean;
  }): Promise<AdvisorQuestionSuggestionResponse[]> {
    try {
      const res = await apiClient.get<AdvisorQuestionSuggestionResponse[]>(
        `/patients/${args.patientId}/advisor-chat/question-suggestions`,
        {
          params: {
            question_date: args.questionDate,
            subject_type: args.subjectType,
            subject_id: args.subjectId,
            force_rebuild: args.forceRebuild,
          },
        },
      );
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status !== undefined && status >= 400 && status < 500) return [];
      throw err;
    }
  },
};
